import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, campaigns as campaignsApi, products as productsApi } from '../api/client.ts';
import { CAMPAIGN_STATUS_LABEL, STAGE_KEYS, STAGE_LABEL } from '../api/types.ts';
import type { CampaignListItem, CampaignStatus, CollectionPlan, ProspectStage } from '../api/types.ts';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  PageHeader,
  Panel,
  Select,
  Spinner,
  useToast,
} from '../ui/primitives.tsx';
import { useDocumentTitle } from '../ui/Logo.tsx';

/**
 * Campanhas.
 *
 * O §39 pede o mínimo de cliques entre a intenção e o resultado, então criar
 * campanha é um formulário na própria tela da lista — não um assistente de
 * várias etapas. Uma campanha é: produto, nichos, região e quantidade.
 *
 * Cada linha é uma campanha com as ações do dia (coletar, qualificar) à vista;
 * o que mexe em muita coisa de uma vez (excluir os leads) pede confirmação com
 * o número na frente e deixa desfazer.
 */
export function Campaigns() {
  useDocumentTitle('Campanhas');
  const [creating, setCreating] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsApi.list(),
  });

  return (
    <>
      <PageHeader
        title="Campanhas"
        description="O que procurar, onde e para vender o quê. Cada campanha junta as empresas de uma busca."
        actions={
          <Button variant="primary" onClick={() => setCreating((open) => !open)}>
            {creating ? 'Cancelar' : 'Nova campanha'}
          </Button>
        }
      />

      {creating && (
        <div className="enter-up mb-6">
          <CampaignForm onDone={() => setCreating(false)} />
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-10 text-[var(--color-ink-faint)]">
          <Spinner label="Carregando campanhas" />
        </div>
      )}
      {error && <Alert>{error instanceof ApiError ? error.message : 'Não foi possível carregar as campanhas.'}</Alert>}
      {data && data.campaigns.length === 0 && !creating && (
        <Panel>
          <EmptyState
            title="Nenhuma campanha ainda"
            description="Uma campanha define o que procurar: produto, nicho, região e quantidade."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                Criar a primeira campanha
              </Button>
            }
          />
        </Panel>
      )}
      {data && data.campaigns.length > 0 && (
        <ul className="flex flex-col gap-3">
          {data.campaigns.map((campaign) => (
            <CampaignRow key={campaign.id} campaign={campaign} />
          ))}
        </ul>
      )}
    </>
  );
}

type Expanded = 'none' | 'plan' | 'settings';

/**
 * Qualificar a campanha inteira enfileira UM job, que percorre os prospects em
 * sequência — a visita a site precisa ser sequencial para não virar rajada de
 * requisições em servidores de empresas reais.
 */
function CampaignRow({ campaign }: { campaign: CampaignListItem }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [expanded, setExpanded] = useState<Expanded>('none');
  const [deleting, setDeleting] = useState(false);
  // O plano fica na tela até a pessoa confirmar: coleta é paga, e confirmar sem
  // ver o número é confirmar no escuro (decisão 24).
  const [plan, setPlan] = useState<CollectionPlan | null>(null);
  const [message, setMessage] = useState<{ text: string; tone: 'ok' | 'danger' } | null>(null);

  const fail = (fallback: string) => (error: unknown) =>
    setMessage({ text: error instanceof ApiError ? error.message : fallback, tone: 'danger' });

  const preflight = useMutation({
    mutationFn: () => campaignsApi.preflight(campaign.id),
    onSuccess: (data) => {
      setMessage(null);
      setPlan(data.plan);
      setExpanded('plan');
    },
    onError: fail('Não foi possível calcular o custo da coleta.'),
  });

  const collect = useMutation({
    mutationFn: () => campaignsApi.collect(campaign.id),
    onSuccess: (data) => {
      setPlan(null);
      setExpanded('none');
      setMessage({ text: data.queued ? 'Coleta na fila.' : 'Já havia uma coleta na fila.', tone: 'ok' });
    },
    onError: (error: unknown) => {
      setPlan(null);
      setExpanded('none');
      fail('A coleta não pôde ser iniciada.')(error);
    },
  });

  const qualify = useMutation({
    mutationFn: () => campaignsApi.qualify(campaign.id),
    onSuccess: (data) =>
      setMessage({ text: data.queued ? 'Qualificação na fila.' : 'A qualificação já estava na fila.', tone: 'ok' }),
    onError: fail('A qualificação não pôde ser iniciada.'),
  });

  const restore = useMutation({
    mutationFn: (batchId: string) => campaignsApi.restoreLeads(campaign.id, batchId),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['prospects'] }),
      ]);
      toast({
        message:
          result.skipped > 0
            ? `${result.restored} leads restaurados. ${result.skipped} já tinham sido cadastrados de novo e ficaram de fora.`
            : `${result.restored} ${result.restored === 1 ? 'lead restaurado' : 'leads restaurados'}.`,
      });
    },
    onError: (error: unknown) =>
      toast({ message: error instanceof ApiError ? error.message : 'Não foi possível restaurar.', tone: 'danger' }),
  });

  const regions = campaign.regions.map((region) => `${region.city}${region.state ? `/${region.state}` : ''}`).join(', ');

  return (
    <li className="rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-surface)]">
      <div className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{campaign.name}</h2>
            <StatusBadge status={campaign.status} />
          </div>
          <p className="mt-1 text-sm text-[var(--color-ink-soft)]">
            {campaign.product_name} para {campaign.niches.join(', ')} em {regions}
          </p>
        </div>

        <dl className="flex shrink-0 gap-6">
          <div>
            <dt className="text-xs text-[var(--color-ink-faint)]">Empresas</dt>
            <dd className="font-display tabular text-lg font-semibold">
              {campaign.prospects}
              <span className="text-sm font-normal text-[var(--color-ink-faint)]"> / {campaign.desired_count}</span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-ink-faint)]">Coletas</dt>
            <dd className="font-display tabular text-lg font-semibold">{campaign.runs}</dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <Link
            to={`/prospects?campaignId=${campaign.id}`}
            className="inline-flex h-9 items-center rounded-[var(--radius-sm)] px-3 text-sm font-medium text-[var(--color-brand-text)] hover:bg-[var(--color-brand-soft)]"
          >
            Ver prospects
          </Link>
          <Button
            loading={preflight.isPending}
            onClick={() => preflight.mutate()}
            title="Mostra quantos places e quanto custa antes de coletar."
          >
            Coletar
          </Button>
          <Button loading={qualify.isPending} onClick={() => qualify.mutate()}>
            Qualificar
          </Button>
          <Button
            variant="ghost"
            aria-expanded={expanded === 'settings'}
            onClick={() => setExpanded((open) => (open === 'settings' ? 'none' : 'settings'))}
          >
            Ajustes
          </Button>
          <Button variant="quiet-danger" onClick={() => setDeleting(true)}>
            Excluir leads
          </Button>
        </div>
      </div>

      {(message || campaign.restorable) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--color-line)] px-5 py-2.5 text-sm">
          {message && (
            <span className={message.tone === 'danger' ? 'text-[var(--color-danger)]' : 'text-[var(--color-ink-soft)]'}>
              {message.text}
            </span>
          )}
          {campaign.restorable && (
            <span className="flex flex-wrap items-center gap-2 text-[var(--color-ink-soft)]">
              {campaign.restorable.count} {campaign.restorable.count === 1 ? 'lead excluído' : 'leads excluídos'} em{' '}
              {new Date(campaign.restorable.deletedAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}.
              <Button
                size="sm"
                variant="ghost"
                loading={restore.isPending}
                onClick={() => restore.mutate(campaign.restorable!.batchId)}
              >
                Restaurar
              </Button>
            </span>
          )}
        </div>
      )}

      {expanded === 'plan' && plan && (
        <CollectPlan
          plan={plan}
          loading={collect.isPending}
          onConfirm={() => collect.mutate()}
          onCancel={() => {
            setPlan(null);
            setExpanded('none');
          }}
        />
      )}
      {expanded === 'settings' && <CampaignSettings campaign={campaign} onDone={() => setExpanded('none')} />}

      <DeleteLeadsDialog campaign={campaign} open={deleting} onClose={() => setDeleting(false)} />
    </li>
  );
}

function CollectPlan({
  plan,
  loading,
  onConfirm,
  onCancel,
}: {
  plan: CollectionPlan;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const blocked = !plan.enabled || plan.estimatedUsd > plan.remainingUsd || plan.places <= 0;
  return (
    <div className="flex flex-col gap-3 border-t border-[var(--color-line)] bg-[var(--color-canvas)] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm">
        {!plan.enabled ? (
          'A coleta pelo Google Maps está desligada no servidor (APIFY_ENABLED=false).'
        ) : (
          <>
            Até <strong className="tabular">{plan.places}</strong> empresas
            {plan.capped && ` (a meta era ${plan.requestedPlaces}; o teto por coleta limita)`}, por cerca de{' '}
            <strong className="tabular">US$ {plan.estimatedUsd.toFixed(2)}</strong>. Restam US${' '}
            {plan.remainingUsd.toFixed(2)} de {plan.monthlyBudgetUsd.toFixed(2)} no mês.
            {plan.fullCollection && (
              <span className="mt-1 block text-[var(--color-ink-soft)]">
                Coleta completa: até US$ {plan.perCompanyUsd.toFixed(4).replace('.', ',')} por empresa, contando a leitura do
                Instagram de quem passar na triagem (cobrada à parte, dentro do mesmo teto).
              </span>
            )}
          </>
        )}
      </p>
      <div className="flex shrink-0 gap-2">
        <Button variant="primary" disabled={blocked} loading={loading} onClick={onConfirm}>
          Confirmar coleta
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

// --- Excluir leads ----------------------------------------------------------------

/** Os que nunca receberam mensagem: é o padrão, e o que quase sempre se quer limpar. */
const DEFAULT_DELETE: ProspectStage[] = ['a_contatar', 'descartados'];

/**
 * Excluir os leads de uma campanha, escolhendo as etapas.
 *
 * O número de cada grupo aparece antes, o botão diz quantos vão sair, e o aviso
 * depois tem "Desfazer". Quem já conversou com você só sai se a caixa for
 * marcada de propósito. A lista de "não contatar" nunca é apagada.
 */
function DeleteLeadsDialog({
  campaign,
  open,
  onClose,
}: {
  campaign: CampaignListItem;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<ProspectStage>>(new Set(DEFAULT_DELETE));
  const breakdown = useQuery({
    queryKey: ['campaigns', campaign.id, 'breakdown'],
    queryFn: () => campaignsApi.leadBreakdown(campaign.id),
    enabled: open,
    staleTime: 0,
  });
  const counts = breakdown.data?.byStage;
  const total = counts ? [...selected].reduce((sum, stage) => sum + (counts[stage] ?? 0), 0) : 0;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
      queryClient.invalidateQueries({ queryKey: ['prospects'] }),
    ]);

  const remove = useMutation({
    mutationFn: () => campaignsApi.deleteLeads(campaign.id, [...selected]),
    onSuccess: async (result) => {
      await refresh();
      onClose();
      setSelected(new Set(DEFAULT_DELETE));
      toast({
        message: `${result.deleted} ${result.deleted === 1 ? 'lead excluído' : 'leads excluídos'} de ${campaign.name}.`,
        action: {
          label: 'Desfazer',
          onClick: async () => {
            try {
              const restored = await campaignsApi.restoreLeads(campaign.id, result.batchId);
              await refresh();
              toast({ message: `${restored.restored} ${restored.restored === 1 ? 'lead restaurado' : 'leads restaurados'}.` });
            } catch (error) {
              toast({ message: error instanceof ApiError ? error.message : 'Não foi possível desfazer.', tone: 'danger' });
            }
          },
        },
      });
    },
  });

  function toggle(stage: ProspectStage) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }

  const contacted = [...selected].some((stage) => !DEFAULT_DELETE.includes(stage) && (counts?.[stage] ?? 0) > 0);

  return (
    <Dialog
      open={open}
      onClose={() => {
        remove.reset();
        onClose();
      }}
      title="Excluir leads da campanha"
      description={`Escolha quais grupos de ${campaign.name} saem. Dá para desfazer depois, pela própria campanha.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="danger" disabled={total === 0} loading={remove.isPending} onClick={() => remove.mutate()}>
            {total === 0 ? 'Nada selecionado' : `Excluir ${total} ${total === 1 ? 'lead' : 'leads'}`}
          </Button>
        </>
      }
    >
      {breakdown.isLoading && (
        <div className="flex justify-center py-6 text-[var(--color-ink-faint)]">
          <Spinner label="Contando leads" />
        </div>
      )}
      {counts && (
        <fieldset className="flex flex-col gap-1">
          <legend className="sr-only">Grupos de leads</legend>
          {STAGE_KEYS.map((stage) => {
            const count = counts[stage] ?? 0;
            return (
              <label
                key={stage}
                className={`flex items-center gap-3 rounded-[var(--radius-sm)] px-2 py-2 ${count === 0 ? 'opacity-50' : 'cursor-pointer hover:bg-[var(--color-hover)]'}`}
              >
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-danger)]"
                  checked={selected.has(stage)}
                  disabled={count === 0}
                  onChange={() => toggle(stage)}
                />
                <span className="flex-1 text-sm">
                  {STAGE_LABEL[stage]}
                  {!DEFAULT_DELETE.includes(stage) && (
                    <span className="text-[var(--color-ink-faint)]"> · já receberam mensagem</span>
                  )}
                </span>
                <span className="font-display tabular text-sm font-semibold">{count}</span>
              </label>
            );
          })}
        </fieldset>
      )}
      {contacted && (
        <div className="mt-3">
          <Alert tone="warn">
            Você marcou empresas com quem já conversou. Elas somem das listas e do funil até serem restauradas.
          </Alert>
        </div>
      )}
      {remove.error && (
        <div className="mt-3">
          <Alert>{remove.error instanceof ApiError ? remove.error.message : 'Não foi possível excluir.'}</Alert>
        </div>
      )}
      <p className="mt-3 text-xs text-[var(--color-ink-faint)]">
        A lista de “não contatar” é mantida: quem pediu para sair continua fora, mesmo se for coletado de novo.
      </p>
    </Dialog>
  );
}

// --- Ajustes ----------------------------------------------------------------------

/**
 * Ajustes que valem só para esta campanha. Campo vazio = volta ao padrão do
 * servidor (`AI_ANALYZE_TOP_N`, `WHATSAPP_SEND_HOURS`, `CAMPAIGN_AUTO_ANALYZE`).
 * O teto diário de envio não entra aqui de propósito: ele protege o número,
 * que é um só.
 */
function CampaignSettings({ campaign, onDone }: { campaign: CampaignListItem; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [topN, setTopN] = useState(campaign.config?.aiTopN ? String(campaign.config.aiTopN) : '');
  const [hours, setHours] = useState(campaign.config?.sendHours ?? '');
  const [auto, setAuto] = useState(
    campaign.config?.autoAnalyze === true ? 'sim' : campaign.config?.autoAnalyze === false ? 'nao' : '',
  );
  const [full, setFull] = useState(
    campaign.config?.fullCollection === true ? 'sim' : campaign.config?.fullCollection === false ? 'nao' : '',
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      campaignsApi.update(campaign.id, {
        config: {
          aiTopN: topN.trim() === '' ? null : Number(topN),
          sendHours: hours.trim() === '' ? null : hours.trim(),
          autoAnalyze: auto === '' ? null : auto === 'sim',
          fullCollection: full === '' ? null : full === 'sim',
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ message: 'Ajustes da campanha salvos.' });
      onDone();
    },
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : 'Não foi possível salvar.'),
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 border-t border-[var(--color-line)] bg-[var(--color-canvas)] px-5 py-4">
      <p className="text-sm text-[var(--color-ink-soft)]">
        Valem só para esta campanha. Em branco, vale o padrão do servidor.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label="Quantos a IA analisa"
          id={`topn-${campaign.id}`}
          type="number"
          min={1}
          max={500}
          placeholder="padrão"
          hint="Os de maior nota técnica."
          value={topN}
          onChange={(event) => setTopN(event.target.value)}
        />
        <Field
          label="Horário de envio"
          id={`hours-${campaign.id}`}
          placeholder="ex.: 10-17"
          hint="Horas cheias, no fuso da operação."
          value={hours}
          onChange={(event) => setHours(event.target.value)}
        />
        <Select
          label="Analisar com IA ao qualificar"
          id={`auto-${campaign.id}`}
          value={auto}
          onChange={setAuto}
          hint="Chamada paga, dentro do teto diário."
          options={[
            { value: '', label: 'Padrão do servidor' },
            { value: 'sim', label: 'Sim, sozinho' },
            { value: 'nao', label: 'Não, eu disparo' },
          ]}
        />
        <Select
          label="Coleta completa"
          id={`full-${campaign.id}`}
          value={full}
          onChange={setFull}
          hint="Traz Instagram e redes do site e lê o Instagram de quem passar. Custa mais por empresa."
          options={[
            { value: '', label: 'Padrão do servidor' },
            { value: 'sim', label: 'Sim, completa' },
            { value: 'nao', label: 'Não, só o Google' },
          ]}
        />
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" loading={save.isPending}>
          Salvar ajustes
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

function StatusBadge({ status }: { status: CampaignStatus }) {
  const tones: Record<CampaignStatus, 'neutral' | 'brand' | 'warn' | 'ok' | 'danger'> = {
    draft: 'neutral',
    running: 'brand',
    paused: 'warn',
    done: 'ok',
    failed: 'danger',
  };
  return <Badge tone={tones[status]}>{CAMPAIGN_STATUS_LABEL[status]}</Badge>;
}

// --- Nova campanha ------------------------------------------------------------------

function CampaignForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list(),
  });

  const [name, setName] = useState('');
  const [productId, setProductId] = useState('');
  const [niches, setNiches] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [desiredCount, setDesiredCount] = useState(50);
  const [error, setError] = useState<string | null>(null);

  const product = productsData?.products.find((item) => item.id === productId);

  // Os nichos alvo do produto viram sugestão clicável: o catálogo já sabe para
  // quem aquele produto serve, e fazer o usuário redigitar isso é desperdício.
  const suggestions = useMemo(() => product?.config.targetNiches ?? [], [product]);

  const mutation = useMutation({
    mutationFn: () =>
      campaignsApi.create({
        name,
        targetProductId: productId,
        niches: niches
          .split(',')
          .map((niche) => niche.trim())
          .filter(Boolean),
        regions: [{ city: city.trim(), ...(state.trim() ? { state: state.trim().toUpperCase() } : {}) }],
        sources: ['csv'],
        desiredCount,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ message: `Campanha ${name} criada.` });
      onDone();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Não foi possível criar a campanha.');
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!productId) {
      setError('Escolha o produto alvo.');
      return;
    }
    mutation.mutate();
  }

  function addSuggestion(niche: string) {
    const current = niches
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (current.includes(niche)) return;
    setNiches([...current, niche].join(', '));
  }

  return (
    <Panel title="Nova campanha" description="Produto, nicho, região e quantidade. A coleta só começa quando você pedir.">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid gap-3 md:grid-cols-2">
          <Field
            label="Nome"
            id="campanha-nome"
            required
            placeholder="Hamburguerias JP — Automação"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Select
            label="Produto alvo"
            id="campanha-produto"
            value={productId}
            onChange={setProductId}
            options={[
              { value: '', label: 'Selecione…' },
              ...(productsData?.products ?? []).map((item) => ({ value: item.id, label: item.name })),
            ]}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Field
            label="Nichos"
            id="campanha-nichos"
            required
            placeholder="hamburgueria, pizzaria"
            hint="Separe por vírgula."
            value={niches}
            onChange={(e) => setNiches(e.target.value)}
          />
          {suggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-[var(--color-ink-faint)]">Sugestões do produto:</span>
              {suggestions.map((niche) => (
                <button
                  key={niche}
                  type="button"
                  onClick={() => addSuggestion(niche)}
                  className="rounded-full border border-[var(--color-line-strong)] px-2.5 py-0.5 text-xs hover:border-[var(--color-brand)] hover:text-[var(--color-brand-text)]"
                >
                  + {niche}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-[2fr_0.6fr_1fr]">
          <Field
            label="Cidade"
            id="campanha-cidade"
            required
            placeholder="João Pessoa"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <Field
            label="UF"
            id="campanha-uf"
            maxLength={2}
            placeholder="PB"
            value={state}
            onChange={(e) => setState(e.target.value)}
          />
          <Field
            label="Quantidade desejada"
            id="campanha-qtd"
            type="number"
            min={1}
            max={1000}
            hint="Até 1000 — a coleta é cobrada por resultado."
            value={desiredCount}
            onChange={(e) => setDesiredCount(Number(e.target.value))}
          />
        </div>

        {error && <Alert>{error}</Alert>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Criar campanha
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
    </Panel>
  );
}
